import { create } from 'zustand';
import type { PublicAuthConfigDTO, SelfUser, UserSettingsDTO } from '@clanfire/shared';
import { authApi } from '@/api/endpoints';
import { applyAppearance } from '@/lib/appearance';

type Status = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: Status;
  user: SelfUser | null;
  settings: UserSettingsDTO | null;
  /** Sign-up rules (invite-only? must confirm email?), known before anyone logs in. */
  config: PublicAuthConfigDTO;
  init: () => Promise<void>;
  setSession: (user: SelfUser, settings: UserSettingsDTO) => void;
  setUser: (user: SelfUser) => void;
  setSettings: (settings: UserSettingsDTO) => void;
  /** Local sign-out (no API call) — used after 401s and revoked sessions. */
  clear: () => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'idle',
  user: null,
  settings: null,
  config: { registration: 'open', emailVerification: 'optional', oauthProviders: [], turnstileSiteKey: null },

  init: async () => {
    if (get().status !== 'idle') return;
    set({ status: 'loading' });
    const config = authApi
      .config()
      .then((c) => set({ config: c }))
      .catch(() => undefined); // keep the defaults; the server still enforces its rules
    try {
      const { user, settings } = await authApi.me();
      await config;
      get().setSession(user, settings);
    } catch {
      await config;
      set({ status: 'unauthenticated', user: null, settings: null });
    }
  },

  setSession: (user, settings) => {
    applyAppearance(settings);
    set({ status: 'authenticated', user, settings });
  },

  setUser: (user) => set({ user }),

  setSettings: (settings) => {
    applyAppearance(settings);
    set({ settings });
  },

  clear: () => set({ status: 'unauthenticated', user: null, settings: null }),
}));

export function useMe(): SelfUser {
  const user = useAuth((s) => s.user);
  if (!user) throw new Error('useMe() called outside an authenticated area');
  return user;
}
